import { DEFAULT_UNDEFINED_NUMBER_DISPLAY } from '@ui/constants/uiConfig';
import { formatNumber, FormatNumberOptions } from '@ui/lib/bignumber';
import { Text, TextProps } from '@ui/lib/park/ui/text';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { forwardRef, Ref, useMemo } from 'react';

export type AmountProps = TextProps & {
  value: Nullable<BigNumber | number>;
  defaultUndefinedDisplay?: string;
  opts?: FormatNumberOptions;
  before?: string;
  after?: string;
};
/**
 * component version of formatNumber
 *
 * accept decimal values in BigNumber type that already divided by their decimal offsets
 *
 * this is intended to standardize the usage of BigNumber to represent decimal values, only use bigint to represent big integer values(as the name suggested)
 * @example
 * const bigIntValue = useContractValue() // bigint(1.234e18)
 * const decimalValue = toReadableValue(bigIntValue, 18) // BigNumber(1.234)
 * return <Amount value={decimalValue} />
 */
const Component = (
  {
    value,
    defaultUndefinedDisplay,
    opts,
    before,
    after,
    ...props
  }: AmountProps,
  ref: Ref<HTMLParagraphElement>,
) => {
  const numberEl = isNil(value) ? null : formatNumber(value, opts ?? {});

  return (
    // V2: every numeral renders in IBM Plex Mono with tabular-nums
    <Text
      ref={ref}
      fontFamily="Inter"
      {...props}
      style={{
        fontVariantNumeric: 'tabular-nums',
        ...props.style,
      }}
    >
      {before}
      {isNil(numberEl) ? defaultUndefinedDisplay ?? '' : numberEl}
      {after}
    </Text>
  );
};

export const Amount = forwardRef(Component);
